import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNum, networkLabel } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Check, X, Filter, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Tabs, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function actionBadge(action) {
  const map = {
    increase: "bg-emerald-50 text-emerald-700 border-emerald-200",
    decrease: "bg-amber-50 text-amber-700 border-amber-200",
    pause: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-mono border uppercase ${map[action] || "border-zinc-200"}`}>
      {action}
    </span>
  );
}

function statusBadge(s) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    executed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-zinc-100 text-zinc-600 border-zinc-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-mono border uppercase ${map[s] || ""}`}>{s}</span>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = tab !== "all" ? { status: tab } : {};
      const r = await api.get("/proposals", { params });
      setProposals(r.data.proposals || []);
    } catch (e) {
      toast.error("Failed to load proposals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (p) => {
    setActionId(p.id);
    try {
      const r = await api.post(`/proposals/${p.id}/approve`);
      if (r.data.ok) toast.success(`Executed: ${p.action} on ${p.campaign_name}`);
      else toast.error(`Failed: ${r.data.error || "unknown"}`);
      load();
    } catch (e) {
      toast.error("Approval failed");
    } finally {
      setActionId(null);
    }
  };

  const reject = async (p) => {
    setActionId(p.id);
    try {
      await api.post(`/proposals/${p.id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setActionId(null); }
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget === "bulk") {
        const r = await api.delete(`/proposals?status=pending`);
        toast.success(`Deleted ${r.data.deleted} pending proposal(s)`);
      } else if (deleteTarget && deleteTarget.id) {
        await api.delete(`/proposals/${deleteTarget.id}`);
        toast.success("Proposal deleted");
      }
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const pendingCount = proposals.filter((p) => p.status === "pending").length;

  return (
    <div data-testid="approvals-page" className="fade-in">
      <PageHeader
        eyebrow="Approval Queue"
        title="Pending & Recent Actions"
        description="Review proposed budget changes from manual-mode rules. Auto-mode proposals show as already executed."
        actions={
          <div className="flex items-center gap-2">
            {tab === "pending" && pendingCount > 0 && (
              <Button
                data-testid="bulk-delete-pending-btn"
                variant="outline"
                onClick={() => setDeleteTarget("bulk")}
                className="rounded-none border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete all pending ({pendingCount})
              </Button>
            )}
            <Button
              data-testid="approvals-refresh-btn"
              onClick={load}
              disabled={loading}
              variant="outline"
              className="rounded-none"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-zinc-100 p-1">
            {["pending", "executed", "failed", "rejected", "all"].map((s) => (
              <TabsTrigger
                key={s}
                value={s}
                data-testid={`approvals-tab-${s}`}
                className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
              >
                {s}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="bg-white border border-zinc-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="proposals-table">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left px-5 py-3 data-label">Campaign</th>
                  <th className="text-left px-5 py-3 data-label">Action</th>
                  <th className="text-left px-5 py-3 data-label">Network</th>
                  <th className="text-right px-5 py-3 data-label">Observed CPA</th>
                  <th className="text-right px-5 py-3 data-label">Current → New</th>
                  <th className="text-left px-5 py-3 data-label">Reason</th>
                  <th className="text-center px-5 py-3 data-label">Status</th>
                  <th className="text-right px-5 py-3 data-label">Decide</th>
                </tr>
              </thead>
              <tbody>
                {proposals.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-zinc-500 font-mono text-sm">
                      No {tab !== "all" ? tab : ""} proposals.
                    </td>
                  </tr>
                )}
                {proposals.map((p) => (
                  <tr key={p.id} data-testid={`proposal-row-${p.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 align-top">
                    <td className="px-5 py-3 max-w-xs">
                      <div className="font-medium text-zinc-900 truncate">{p.campaign_name}</div>
                      <div className="text-xs text-zinc-500 font-mono">
                        Rule: {p.rule_name}
                      </div>
                    </td>
                    <td className="px-5 py-3">{actionBadge(p.action)}</td>
                    <td className="px-5 py-3 font-mono text-xs uppercase">{networkLabel(p.network)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtMoney(p.observed_cpa)}</td>
                    <td className="px-5 py-3 text-right font-mono">
                      {p.action === "pause" ? (
                        <span className="text-red-700">→ PAUSE</span>
                      ) : (
                        <span>
                          {p.current_budget != null ? fmtMoney(p.current_budget) : "—"} → {p.new_budget != null ? fmtMoney(p.new_budget) : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 max-w-xs text-xs text-zinc-600">{p.reason}</td>
                    <td className="px-5 py-3 text-center">{statusBadge(p.status)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {p.status === "pending" ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            data-testid={`approve-${p.id}`}
                            size="sm"
                            disabled={actionId === p.id}
                            onClick={() => approve(p)}
                            className="rounded-none bg-zinc-900 hover:bg-zinc-800 h-8 text-xs"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            data-testid={`reject-${p.id}`}
                            size="sm"
                            variant="outline"
                            disabled={actionId === p.id}
                            onClick={() => reject(p)}
                            className="rounded-none h-8 text-xs border-zinc-300"
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                          <Button
                            data-testid={`delete-${p.id}`}
                            size="sm"
                            variant="ghost"
                            disabled={actionId === p.id}
                            onClick={() => setDeleteTarget(p)}
                            className="rounded-none h-8 text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-zinc-400 font-mono">
                            {p.executed_at ? new Date(p.executed_at).toLocaleString() : "—"}
                          </span>
                          <Button
                            data-testid={`delete-${p.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteTarget(p)}
                            className="rounded-none h-7 text-xs text-zinc-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-zinc-300" data-testid="delete-proposal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">
              {deleteTarget === "bulk" ? "Delete all pending proposals" : "Delete proposal"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget === "bulk"
                ? `Are you sure you want to delete all ${pendingCount} pending proposal(s)? This cannot be undone.`
                : deleteTarget
                ? <>Are you sure you want to delete the <span className="font-mono">{deleteTarget.action}</span> proposal for <span className="font-mono text-zinc-900">"{deleteTarget.campaign_name}"</span>? This cannot be undone.</>
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-proposal-btn"
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
            >
              Delete{deleteTarget === "bulk" ? " all" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
